import { useState } from "react";
import { Field, FieldGroup, Fieldset, Label } from "../../design/fieldset";
import { Input } from "../../design/input";
import { Button } from "../../design/button";
import SelectArtist from "../../components/SelectArtist";
import { Select } from "../../design/select";
import axios from "axios";
import { AuctionRecord } from "../../models/AuctionRecord";
import PricePerCMSquaredChart from "./components/PricePerCMSquaredChart";
import AverageHammerToMidpointRatioChart from "./components/AverageHammerToMidpointRatioChart";
import moment from "moment";
import { Link } from "../../design/link";

interface MidpointComparison {
  _id: { year: number; month: number };
  avg_hammer_to_midpoint_ratio: number;
}

interface PricePerCMSquared {
  _id: { year: number; month: number };
  price_per_cm: number;
}

interface APIResponse {
  summary?: string;
  pricePerCMSquared: PricePerCMSquared[];
  midpointComparison: MidpointComparison[];
  auctionRecords: AuctionRecord[];
}

interface Prediction {
  currentPrice: number;
  predictions: {
    fiveYear: { conservative: number; moderate: number; optimistic: number };
    tenYear: { conservative: number; moderate: number; optimistic: number };
  };
  probabilityOfProfit?: { fiveYear: number; tenYear: number };
  modelDetails: { cagr: number; avgAnnualReturn: number; volatility: number };
}

const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
};

const formatPercent = (value: number): string => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const ValuationPage: React.FC = () => {
  const [result, setResult] = useState<APIResponse | null>(null);
  const [prediction, setPrediction] = useState<Prediction | null>(null);
  const [artist, setArtist] = useState<any>(null);
  const [category, setCategory] = useState<string>("painting");
  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>(0);
  const [depth, setDepth] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!artist || !category || !width || !height) {
      setError("Please complete all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: valuationData } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/valuation_analysis`,
        { artist: artist.objectID, category, width, height, depth }
      );
      setResult(valuationData.data);

      try {
        const { data: predictionData } = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/v1/analytics/predict`,
          { artist: artist.objectID, category, width, height }
        );
        setPrediction(predictionData.data);
      } catch (err) {
        console.error("Prediction error:", err);
      }

      setShowResults(true);
    } catch (err) {
      console.error(err);
      setError("Analysis failed. Please try again.");
    }
    setIsSubmitting(false);
  };

  const resetForm = () => {
    setResult(null);
    setPrediction(null);
    setShowResults(false);
    setArtist(null);
    setWidth(0);
    setHeight(0);
    setDepth(0);
    setError(null);
  };

  const getRiskLevel = (volatility: number) => {
    if (volatility > 30) return { label: "High", color: "text-rose-700 bg-rose-50 border-rose-200" };
    if (volatility > 20) return { label: "Elevated", color: "text-amber-700 bg-amber-50 border-amber-200" };
    if (volatility > 10) return { label: "Moderate", color: "text-blue-700 bg-blue-50 border-blue-200" };
    return { label: "Low", color: "text-emerald-700 bg-emerald-50 border-emerald-200" };
  };

  // Form View
  if (!showResults) {
    return (
      <div className="min-h-screen bg-gallery-50">
        {/* Header */}
        <div className="bg-white border-b border-gallery-200">
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="max-w-3xl">
              <p className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-3">
                Valuation & Risk Analysis
              </p>
              <h1 className="font-display text-display-sm text-gallery-900 mb-4">
                Professional Art Valuation
              </h1>
              <p className="text-gallery-600 leading-relaxed">
                Institutional-grade valuation methodology combining Monte Carlo simulation,
                historical price analysis, and market trend data. Designed for collectors,
                insurers, and investment professionals.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit}>
                <div className="bg-white border border-gallery-200 rounded-sm">
                  <div className="px-8 py-6 border-b border-gallery-100">
                    <h2 className="font-display text-lg text-gallery-900">Artwork Details</h2>
                    <p className="text-sm text-gallery-500 mt-1">
                      Enter the specifications of the artwork for analysis
                    </p>
                  </div>

                  <div className="px-8 py-6">
                    {error && (
                      <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-sm">
                        <p className="text-sm text-rose-700">{error}</p>
                      </div>
                    )}

                    <Fieldset>
                      <FieldGroup>
                        {/* Artist Selection */}
                        <Field>
                          <Label className="text-sm font-medium text-gallery-700 mb-2 block">
                            Artist <span className="text-rose-500">*</span>
                          </Label>
                          {artist && (
                            <div className="flex items-center gap-4 p-4 mb-3 bg-gallery-50 border border-gallery-200 rounded-sm">
                              {artist.small_image_url && (
                                <img
                                  src={artist.small_image_url}
                                  alt={artist.name}
                                  className="w-12 h-12 rounded-full object-cover"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gallery-900 truncate">{artist.name}</p>
                                <p className="text-xs text-gallery-500">Selected Artist</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => setArtist(null)}
                                className="text-gallery-400 hover:text-gallery-600"
                              >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          )}
                          <SelectArtist artist={artist} onSelectArtist={setArtist} />
                        </Field>

                        {/* Category */}
                        <Field>
                          <Label className="text-sm font-medium text-gallery-700 mb-2 block">
                            Category <span className="text-rose-500">*</span>
                          </Label>
                          <Select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full"
                          >
                            <option value="painting">Painting</option>
                            <option value="sculpture">Sculpture</option>
                            <option value="drawing">Works on Paper</option>
                            <option value="print">Print / Multiple</option>
                            <option value="photograph">Photograph</option>
                          </Select>
                        </Field>

                        {/* Dimensions */}
                        <div>
                          <Label className="text-sm font-medium text-gallery-700 mb-2 block">
                            Dimensions (cm) <span className="text-rose-500">*</span>
                          </Label>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <Input
                                type="number"
                                value={width || ""}
                                onChange={(e) => setWidth(parseInt(e.target.value) || 0)}
                                placeholder="Width"
                              />
                              <p className="text-xs text-gallery-400 mt-1">Width</p>
                            </div>
                            <div>
                              <Input
                                type="number"
                                value={height || ""}
                                onChange={(e) => setHeight(parseInt(e.target.value) || 0)}
                                placeholder="Height"
                              />
                              <p className="text-xs text-gallery-400 mt-1">Height</p>
                            </div>
                            <div>
                              <Input
                                type="number"
                                value={depth || ""}
                                onChange={(e) => setDepth(parseInt(e.target.value) || 0)}
                                placeholder="Depth"
                              />
                              <p className="text-xs text-gallery-400 mt-1">Depth (opt.)</p>
                            </div>
                          </div>
                        </div>
                      </FieldGroup>
                    </Fieldset>
                  </div>

                  <div className="px-8 py-6 bg-gallery-50 border-t border-gallery-100">
                    <Button
                      type="submit"
                      className="w-full sm:w-auto"
                      disabled={isSubmitting || !artist || !width || !height}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Generating Analysis...
                        </span>
                      ) : (
                        "Generate Valuation Report"
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </div>

            {/* Sidebar - Methodology */}
            <div className="space-y-6">
              <div className="bg-white border border-gallery-200 rounded-sm p-6">
                <h3 className="font-display text-sm text-gallery-900 mb-4">Methodology</h3>
                <ul className="space-y-3">
                  {[
                    { title: "Monte Carlo Simulation", desc: "10,000+ price path scenarios" },
                    { title: "Comparable Analysis", desc: "Historical auction data matching" },
                    { title: "Risk Metrics", desc: "Volatility & probability assessment" },
                    { title: "Market Context", desc: "Current trend analysis" },
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-gallery-400 mt-2 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gallery-800">{item.title}</p>
                        <p className="text-xs text-gallery-500">{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gallery-900 rounded-sm p-6 text-white">
                <p className="text-xs font-medium uppercase tracking-wider text-gallery-400 mb-2">
                  Data Coverage
                </p>
                <p className="font-display text-2xl mb-1">1M+</p>
                <p className="text-sm text-gallery-300">Auction Records Analyzed</p>
                <div className="h-px bg-gallery-700 my-4" />
                <p className="text-xs text-gallery-400">
                  Data from Christie's, Sotheby's, Phillips, and 200+ auction houses worldwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results View
  return (
    <div className="min-h-screen bg-gallery-50">
      {/* Header */}
      <div className="bg-white border-b border-gallery-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-2">
                Valuation Report
              </p>
              <h1 className="font-display text-2xl text-gallery-900">
                {artist?.name}
              </h1>
              <p className="text-gallery-500 mt-1">
                {category.charAt(0).toUpperCase() + category.slice(1)} · {width} × {height} cm
              </p>
            </div>
            <Button onClick={resetForm} outline>
              New Analysis
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* AI Summary */}
        {result?.summary && (
          <div className="bg-white border border-gallery-200 rounded-sm p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-gallery-900 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h2 className="font-display text-lg text-gallery-900 mb-3">Market Analysis</h2>
                <p className="text-gallery-600 leading-relaxed whitespace-pre-line">
                  {result.summary}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Prediction Cards */}
        {prediction && (
          <>
            {/* Key Metrics Strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white border border-gallery-200 rounded-sm p-5">
                <p className="stat-label">Est. Current Value</p>
                <p className="stat-value text-xl mt-1">{formatCurrency(prediction.currentPrice)}</p>
              </div>
              <div className="bg-white border border-gallery-200 rounded-sm p-5">
                <p className="stat-label">CAGR</p>
                <p className={`stat-value text-xl mt-1 ${prediction.modelDetails.cagr >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {formatPercent(prediction.modelDetails.cagr)}
                </p>
              </div>
              <div className="bg-white border border-gallery-200 rounded-sm p-5">
                <p className="stat-label">Avg Annual Return</p>
                <p className={`stat-value text-xl mt-1 ${prediction.modelDetails.avgAnnualReturn >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {formatPercent(prediction.modelDetails.avgAnnualReturn)}
                </p>
              </div>
              <div className="bg-white border border-gallery-200 rounded-sm p-5">
                <p className="stat-label">Volatility Risk</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-sm border ${getRiskLevel(prediction.modelDetails.volatility).color}`}>
                    {getRiskLevel(prediction.modelDetails.volatility).label}
                  </span>
                  <span className="text-sm text-gallery-600">{prediction.modelDetails.volatility.toFixed(1)}%</span>
                </div>
              </div>
            </div>

            {/* Forecast Tables */}
            <div className="grid lg:grid-cols-2 gap-6 mb-8">
              {/* 5-Year Forecast */}
              <div className="bg-white border border-gallery-200 rounded-sm">
                <div className="px-6 py-4 border-b border-gallery-100">
                  <h3 className="font-display text-lg text-gallery-900">5-Year Forecast</h3>
                  {prediction.probabilityOfProfit && (
                    <p className="text-sm text-gallery-500 mt-0.5">
                      {prediction.probabilityOfProfit.fiveYear}% probability of positive return
                    </p>
                  )}
                </div>
                <div className="p-6">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left">
                        <th className="pb-3 text-xs font-medium uppercase tracking-wider text-gallery-500">Scenario</th>
                        <th className="pb-3 text-xs font-medium uppercase tracking-wider text-gallery-500 text-right">Projected Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gallery-100">
                      <tr>
                        <td className="py-3 text-sm text-gallery-600">Conservative (10th percentile)</td>
                        <td className="py-3 text-sm font-medium text-gallery-900 text-right">{formatCurrency(prediction.predictions.fiveYear.conservative)}</td>
                      </tr>
                      <tr className="bg-gallery-50">
                        <td className="py-3 text-sm font-medium text-gallery-900">Moderate (50th percentile)</td>
                        <td className="py-3 font-display text-lg text-gallery-900 text-right">{formatCurrency(prediction.predictions.fiveYear.moderate)}</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-sm text-gallery-600">Optimistic (90th percentile)</td>
                        <td className="py-3 text-sm font-medium text-gallery-900 text-right">{formatCurrency(prediction.predictions.fiveYear.optimistic)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 10-Year Forecast */}
              <div className="bg-white border border-gallery-200 rounded-sm">
                <div className="px-6 py-4 border-b border-gallery-100">
                  <h3 className="font-display text-lg text-gallery-900">10-Year Forecast</h3>
                  {prediction.probabilityOfProfit && (
                    <p className="text-sm text-gallery-500 mt-0.5">
                      {prediction.probabilityOfProfit.tenYear}% probability of positive return
                    </p>
                  )}
                </div>
                <div className="p-6">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left">
                        <th className="pb-3 text-xs font-medium uppercase tracking-wider text-gallery-500">Scenario</th>
                        <th className="pb-3 text-xs font-medium uppercase tracking-wider text-gallery-500 text-right">Projected Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gallery-100">
                      <tr>
                        <td className="py-3 text-sm text-gallery-600">Conservative (10th percentile)</td>
                        <td className="py-3 text-sm font-medium text-gallery-900 text-right">{formatCurrency(prediction.predictions.tenYear.conservative)}</td>
                      </tr>
                      <tr className="bg-gallery-50">
                        <td className="py-3 text-sm font-medium text-gallery-900">Moderate (50th percentile)</td>
                        <td className="py-3 font-display text-lg text-gallery-900 text-right">{formatCurrency(prediction.predictions.tenYear.moderate)}</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-sm text-gallery-600">Optimistic (90th percentile)</td>
                        <td className="py-3 text-sm font-medium text-gallery-900 text-right">{formatCurrency(prediction.predictions.tenYear.optimistic)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Charts */}
        {result && (
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border border-gallery-200 rounded-sm p-6">
              <h3 className="font-display text-lg text-gallery-900 mb-4">Price Per CM² Trend</h3>
              <div className="h-64">
                <PricePerCMSquaredChart pricePerCMSquared={result.pricePerCMSquared} />
              </div>
            </div>
            <div className="bg-white border border-gallery-200 rounded-sm p-6">
              <h3 className="font-display text-lg text-gallery-900 mb-4">Hammer vs Estimate Performance</h3>
              <div className="h-64">
                <AverageHammerToMidpointRatioChart midpointComparison={result.midpointComparison} />
              </div>
            </div>
          </div>
        )}

        {/* Comparable Sales */}
        {result && result.auctionRecords.length > 0 && (
          <div className="bg-white border border-gallery-200 rounded-sm">
            <div className="px-6 py-4 border-b border-gallery-100 flex items-center justify-between">
              <div>
                <h3 className="font-display text-lg text-gallery-900">Comparable Sales</h3>
                <p className="text-sm text-gallery-500">{result.auctionRecords.length} relevant auction results</p>
              </div>
            </div>
            <div className="divide-y divide-gallery-100">
              {result.auctionRecords.slice(0, 10).map((record) => (
                <div key={record._id} className="p-6 flex gap-6 hover:bg-gallery-50 transition-colors">
                  {record.medium_image_url && (
                    <img
                      src={record.medium_image_url}
                      alt={record.title}
                      className="w-24 h-24 object-cover rounded-sm shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gallery-900 truncate">{record.title}</p>
                    <p className="text-sm text-gallery-500 mt-0.5">{record.medium}</p>
                    <p className="text-xs text-gallery-400 mt-1">{record.size}</p>
                  </div>
                  <div className="text-right shrink-0">
                    {record.usd_hammer_price && (
                      <p className="font-display text-lg text-gallery-900">
                        {formatCurrency(record.usd_hammer_price / 100)}
                      </p>
                    )}
                    {record.usd_estimate_low && record.usd_estimate_high && (
                      <p className="text-xs text-gallery-500 mt-0.5">
                        Est. {formatCurrency(record.usd_estimate_low / 100)} - {formatCurrency(record.usd_estimate_high / 100)}
                      </p>
                    )}
                    {record.sale_date && (
                      <p className="text-xs text-gallery-400 mt-1">
                        {moment(record.sale_date).format("MMM YYYY")}
                      </p>
                    )}
                    {record.website_url && (
                      <Link href={record.website_url} target="_blank" className="text-xs text-gallery-600 hover:text-gallery-900 mt-2 inline-block">
                        View lot details
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-8 p-6 bg-gallery-100 rounded-sm">
          <p className="text-xs text-gallery-500 leading-relaxed">
            <strong className="text-gallery-700">Important Notice:</strong> This valuation report is generated using
            statistical models and historical market data. It is intended for informational purposes only and should
            not be construed as financial advice. Past performance does not guarantee future results. Art valuations
            are inherently subjective and actual sale prices may vary significantly based on condition, provenance,
            market conditions, and other factors. Consult with qualified professionals before making investment decisions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ValuationPage;
